/**
 * API Types for UI-Model-1 (Authoritative Type Definitions)
 *
 * This module defines the AUTHORITATIVE types based on the actual JSON API structure.
 * These types are independent of the legacy UI-Model types and represent the
 * canonical shape of data as it flows through the API.
 *
 * Architecture:
 * - Fetch → Store API response as-is in state
 * - Render → Components read from API format state
 * - Edit → Commands modify API format state directly
 * - Save → Send state sections to API (already in correct format)
 *
 * @see Plan: UI-Model-1 Type Architecture - Phase 5
 */

// ============================================================================
// Basic Type Constants
// ============================================================================

/**
 * Item types - discriminator values for the item union
 */
export type ItemType = 'field' | 'category' | 'connector' | 'widget';

/**
 * Field types supported by the system
 */
export type FieldType =
  | 'text'
  | 'integer'
  | 'decimal'
  | 'boolean'
  | 'fraction'
  | 'range'
  | 'multichoice';

/**
 * Widget types supported by the system
 */
export type WidgetType =
  | 'button'
  | 'contentBlock'
  | 'divider'
  | 'yourBuild'
  | 'build';

// ============================================================================
// Item Types - Discriminated Union
// ============================================================================

/**
 * Base properties shared by all item types
 */
interface BaseItemAPI {
  /** Unique identifier for the item */
  id: string;
  /** Whether this item can be reused across containers */
  isReusable: boolean;
  /**
   * Reference to the parent submodel
   * OPTIONAL - set when submodel exists, undefined for visual-only widgets
   */
  parentId?: string;
  /** Item display configuration */
  configuration?: Record<string, unknown>;
  /** Optional ordering within container */
  order?: number;
}

/**
 * Choice option for multichoice fields
 */
export interface FieldChoiceOption {
  av_id: string;
  av_value: string;
  display_value?: string;
  product_image?: string;
  visibility?: boolean;
}

/**
 * Multi-filter configuration for fields
 */
export interface MultiFilterConfig {
  filters: unknown[];
  logic: string;
  type: string;
}

/**
 * Data configuration for fields (from Model Spec)
 */
export interface FieldDataConfiguration {
  choices?: FieldChoiceOption[];
  enableCalculation?: boolean;
  filterAvailable?: boolean;
  multiFilter?: MultiFilterConfig;
  minCount?: number;
  maxCount?: number;
  minQuantity?: number;
  maxQuantity?: number;
  minValue?: number;
  maxValue?: number;
  step?: number;
  defaultValue?: unknown;
  unit?: string;
  precision?: number;
}

/**
 * Field item - represents data input fields from Model Spec
 */
export interface FieldAPI extends BaseItemAPI {
  type: 'field';
  /** Display name of the field */
  name: string;
  /** Type of field (text, integer, multichoice, etc.) */
  fieldType: FieldType | string; // string allows for future extensibility
  /** Data configuration from Model Spec (choices, validation rules, etc.) */
  dataConfiguration?: FieldDataConfiguration;
}

/**
 * Category item - represents product selection from catalog
 */
export interface CategoryAPI extends BaseItemAPI {
  type: 'category';
  /** Display name of the category */
  name: string;
  /** Reference to product catalog category - REQUIRED */
  categoryId: string;
  /** Optional field type indicator */
  fieldType?: 'category';
  /** Category-specific data configuration */
  dataConfiguration?: Record<string, unknown>;
}

/**
 * Connector item - links between model parts
 */
export interface ConnectorAPI extends BaseItemAPI {
  type: 'connector';
  /** Display name of the connector */
  name: string;
  /** Optional field type indicator */
  fieldType?: 'connector';
}

/**
 * Widget item - visual UI elements (dividers, buttons, build summary)
 */
export interface WidgetAPI extends BaseItemAPI {
  type: 'widget';
  /** Type of widget (button, divider, yourBuild, etc.) */
  widgetType: WidgetType | string; // string allows for future extensibility
  /** Optional display name for the widget */
  name?: string;
  /** Optional HTML content (for contentBlock) */
  content?: string;
  /** Reference to sharedWidgets configuration */
  configRef?: string;
}

/**
 * Nested container reference in items array (for API compatibility)
 * This appears in the items array when nestedContainers are present
 */
export interface NestedContainerItemAPI {
  nestedContainers: ContainerAPI[];
}

/**
 * Union type for all items in a container
 */
export type ItemAPI = FieldAPI | CategoryAPI | ConnectorAPI | WidgetAPI;

/**
 * Union type for all items including nested container reference
 * Used for API compatibility - the items array can contain this reference
 */
export type ContainerItemAPI = ItemAPI | NestedContainerItemAPI;

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard for FieldAPI
 */
export const isFieldAPI = (item: ItemAPI | ContainerItemAPI): item is FieldAPI =>
  'type' in item && item.type === 'field';

/**
 * Type guard for CategoryAPI
 */
export const isCategoryAPI = (item: ItemAPI | ContainerItemAPI): item is CategoryAPI =>
  'type' in item && item.type === 'category';

/**
 * Type guard for ConnectorAPI
 */
export const isConnectorAPI = (item: ItemAPI | ContainerItemAPI): item is ConnectorAPI =>
  'type' in item && item.type === 'connector';

/**
 * Type guard for WidgetAPI
 */
export const isWidgetAPI = (item: ItemAPI | ContainerItemAPI): item is WidgetAPI =>
  'type' in item && item.type === 'widget';

/**
 * Type guard for NestedContainerItemAPI
 */
export const isNestedContainerItemAPI = (
  item: ContainerItemAPI
): item is NestedContainerItemAPI =>
  'nestedContainers' in item && !('type' in item);

/**
 * Type guard to check if item has a type property (is a proper ItemAPI)
 */
export const isItemAPI = (item: ContainerItemAPI): item is ItemAPI =>
  'type' in item && ['field', 'category', 'connector', 'widget'].includes(item.type);

// ============================================================================
// Submodel Types
// ============================================================================

/**
 * Submodel configuration options
 */
export interface SubmodelConfigurationAPI {
  /** Show submodel title in UI */
  showTitle?: boolean;
  /** Display name override */
  displayName?: string;
  /** Internal field name */
  fieldName?: string;
  /** Hide container if no products available */
  hideIfNoProducts?: boolean;
  /** How to handle excluded state */
  exclusionType?: 'hide' | 'disable';
  /** Visibility state */
  isVisible?: boolean;
}

/**
 * Full submodel definition (for ROOT container - owns the configuration)
 */
export interface SubmodelFullAPI {
  id: string;
  name: string;
  isTemporary?: boolean;
  /**
   * Max instance count from the Data Model spec (TSubmodel.data.maxInstantiation).
   * OPTIONAL — when present it lets container-locking rules (multi-instantiation
   * cascade + max propagation) read the value off the binding; when absent,
   * resolve it from the spec via ModuleContext.getSubmodelInstanceLimits().
   * Treated as 1 when missing.
   */
  maxInstantiation?: number;
  /** Min instance count from the Data Model spec. Treated as 1 when missing. */
  minInstantiation?: number;
  /** Full configuration - REQUIRED for root containers */
  configuration: SubmodelConfigurationAPI;
}

/**
 * Reference-only submodel (for NESTED containers - inherits from parent)
 */
export interface SubmodelRefAPI {
  /** Reference to parent's submodel ID */
  id: string;
  // Note: name, isTemporary, and configuration are inherited from parent
}

/**
 * Union type - containers can have either full or reference submodel
 */
export type SubmodelAPI = SubmodelFullAPI | SubmodelRefAPI;

/**
 * Type guard for full submodel (has configuration)
 */
export const isSubmodelFull = (
  submodel: SubmodelAPI | undefined | null
): submodel is SubmodelFullAPI =>
  submodel !== null &&
  submodel !== undefined &&
  'configuration' in submodel &&
  submodel.configuration !== undefined;

/**
 * Type guard for reference submodel (only has id)
 */
export const isSubmodelRef = (
  submodel: SubmodelAPI | undefined | null
): submodel is SubmodelRefAPI =>
  submodel !== null &&
  submodel !== undefined &&
  'id' in submodel &&
  !('configuration' in submodel);

// ============================================================================
// Container Types
// ============================================================================

/**
 * Container configuration - defines layout and appearance
 */
export interface ContainerConfigurationAPI {
  /** Width as percentage or pixel value (e.g., "100%", "50%", "300px") */
  width: string;
  /** Height - "auto" or pixel value */
  height: string;
  /** Content alignment */
  alignment: 'left' | 'center' | 'right';
  /** Item layout direction */
  layout: 'vertical' | 'horizontal';
  /** Show container title */
  showTitle: boolean;
  /** Lock width from editing */
  widthLocked?: boolean;
  /** Enable filter behavior */
  behaveAsFilter?: boolean;
  /** Enable collapsible behavior */
  collapsible?: boolean;
  /** Collapse the container by default on initial render */
  collapseByDefault?: boolean;
  /** Show border around container */
  showBorder?: boolean;
  /** Border placement - ['all'] or combination of ['left','right','top','bottom'] */
  border?: string[];
}

/**
 * Container metadata - structural information
 */
export interface ContainerMetadataAPI {
  /** Container type discriminator */
  type: 'container' | 'nested-container';
  /** Whether this is a nested container */
  isNested: boolean;
  /** Parent container ID (for nested containers) */
  parentContainerId?: string;
  /** Row index for grid layout (optional - defaults to 0) */
  rowIndex?: number;
  /** Nesting depth level (0 = root, 1 = nested, 2 = deeply nested) */
  nestingLevel?: number;
  /** Orphaned state - when submodel was deleted */
  isOrphaned?: boolean;
  /** Name of deleted submodel for warning display */
  orphanedSubmodelName?: string;
}

/**
 * Used component tracking for usageInfo
 */
export interface UsedComponentAPI {
  id: string;
  type: ItemType | 'submodel';
  originalComponentId: string;
  isReusable: boolean;
  parentId?: string;
  content?: string;
}

/**
 * Container content - holds items, nested containers, and submodel
 */
export interface ContainerContentAPI {
  /** Items in this container (fields, categories, connectors, widgets) */
  items: ContainerItemAPI[];
  /** Nested child containers */
  nestedContainers?: ContainerAPI[];
  /** Submodel binding (data source reference) */
  submodel?: SubmodelAPI;
  /**
   * ALL submodels bound to this container, each with its own configuration —
   * written only for free-style models, where a container may mix submodels.
   * The first entry mirrors `submodel` (the first-stamped binding); restricted
   * models never carry this key, keeping their JSON unchanged.
   */
  submodels?: SubmodelFullAPI[];
}

/**
 * Complete container definition
 */
export interface ContainerAPI {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Order within the canvas/parent */
  order: number;
  /** Row index for multi-row layouts */
  rowIndex?: number;
  /** Layout and appearance configuration */
  configuration: ContainerConfigurationAPI;
  /** Content: items, nested containers, and submodel */
  content: ContainerContentAPI;
  /** Structural metadata */
  metadata?: ContainerMetadataAPI;
  /** Component usage tracking */
  usageInfo: {
    usedComponents: UsedComponentAPI[];
  };
}

// ============================================================================
// Canvas & Navigation Types
// ============================================================================

/**
 * Canvas - represents a single page's content area
 */
export interface CanvasAPI {
  /** Associated page ID */
  pageId: string;
  /** Associated group ID */
  groupId: string;
  /** Top-level containers on this canvas */
  containers: ContainerAPI[];
}

/**
 * Canvas map - canvasId to canvas lookup
 */
export type CanvasMapAPI = Record<string, CanvasAPI>;

/**
 * Page configuration options
 */
export interface PageConfigurationAPI {
  /** Automatically advance to next page on completion */
  autoAdvance: boolean;
  /** Page display name */
  name: string;
}

/**
 * Page definition
 */
export interface PageAPI {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Associated canvas ID */
  canvasId: string;
  /** Order within the group */
  order: number;
  /** Page configuration */
  configuration: PageConfigurationAPI;
}

/**
 * Group configuration options
 */
export interface GroupConfigurationAPI {
  /** Show group title */
  showTitle: boolean;
  /** Group display name */
  name: string;
}

/**
 * Group definition - contains multiple pages
 */
export interface GroupAPI {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Order within navigation */
  order: number;
  /** Page orientation within group */
  orientation: 'horizontal' | 'vertical' | string;
  /** Group configuration */
  configuration: GroupConfigurationAPI;
  /** Pages in this group */
  pages: PageAPI[];
}

/**
 * Navigation configuration options
 * NOTE: Properties are optional to match API response flexibility
 */
export interface NavigationConfigurationAPI {
  /** Navigation bar orientation */
  orientation?: 'Horizontal' | 'Vertical';
  /** Visual style */
  style?: 'Block' | 'Stepper';
  /** Text alignment */
  textAlignment?: 'Left' | 'Centre' | 'Right';
  /** Show divider between groups */
  showDivider?: boolean;
  /** Spacing between pages (pixels) */
  pageSpacing?: number;
  /** Spacing between groups (pixels) */
  groupSpacing?: number;
  /** Show/hide navigation */
  showNavigation?: boolean;
  /** Navigation flow type */
  flow?: 'Linear' | 'Nonlinear';
  /** Which page to open when configuration is incomplete */
  incompleteOpeningPage?: 'first-page-always' | 'first-incomplete-page' | 'first-page-with-selected-product';
  /** Which page to open when configuration is complete */
  completeOpeningPage?: 'first-page-always' | 'first-page-with-selected-product';
}

/**
 * Navigation definition
 */
export interface NavigationAPI {
  /** Navigation configuration (may be in configuration object or at root level) */
  configuration?: NavigationConfigurationAPI;
  // Top-level navigation props (flattened in API response for backwards compatibility)
  orientation?: 'Horizontal' | 'Vertical';
  style?: 'Block' | 'Stepper';
  textAlignment?: 'Left' | 'Centre' | 'Right';
  showDivider?: boolean;
  pageSpacing?: number;
  groupSpacing?: number;
  showNavigation?: boolean;
  flow?: 'Linear' | 'Nonlinear';
  incompleteOpeningPage?: 'first-page-always' | 'first-incomplete-page' | 'first-page-with-selected-product';
  completeOpeningPage?: 'first-page-always' | 'first-page-with-selected-product';
  /** Navigation groups */
  groups: GroupAPI[];
}

// ============================================================================
// Header & Footer Types
// ============================================================================

/**
 * Header button configuration
 */
export interface HeaderButtonConfigAPI {
  showText?: boolean;
  buttonLabel?: string;
  showIcon?: boolean;
  iconUrl?: string;
  showTooltip?: boolean;
  tooltipText?: string;
}

/**
 * Header configuration options
 */
export interface HeaderConfigurationAPI {
  /** Show/hide header */
  showHeader: boolean;
  /** Show/hide title */
  showTitle: boolean;
  /** Title text content */
  titleText: string;
  /** Title alignment */
  titleLayout: 'Centre' | 'Left' | 'Right';
  /** Logo image URL */
  logoUrl: string;
  /** Reset button settings */
  showResetButton: boolean;
  resetButtonText: string;
  resetButtonShowIcon: boolean;
  resetButtonIconUrl?: string;
  resetButtonShowTooltip: boolean;
  resetButtonTooltipText?: string;
  /** Close button settings */
  showCloseButton: boolean;
  closeButtonText: string;
  closeButtonShowIcon: boolean;
  closeButtonIconUrl?: string;
  closeButtonShowTooltip: boolean;
  closeButtonTooltipText?: string;
  /** Save button settings (optional) */
  showSaveButton?: boolean;
  saveButtonText?: string;
  saveButtonShowIcon?: boolean;
  saveButtonIconUrl?: string;
  saveButtonShowTooltip?: boolean;
  saveButtonTooltipText?: string;
  /** Your Build mini widget in header */
  showYourBuild?: boolean;
}

/**
 * Header definition
 */
export interface HeaderAPI {
  configuration: HeaderConfigurationAPI;
}

/**
 * Footer configuration options
 */
export interface FooterConfigurationAPI {
  /** Show/hide footer */
  showFooter: boolean;
  /** Description settings */
  showDescription?: boolean;
  descriptionText?: string;
  /** Previous button settings */
  showPreviousButton?: boolean;
  previousButtonText?: string;
  previousButtonShowIcon?: boolean;
  previousButtonIconUrl?: string;
  previousButtonShowTooltip?: boolean;
  previousButtonTooltipText?: string;
  /** Next button settings */
  showNextButton?: boolean;
  nextButtonText?: string;
  nextButtonShowIcon?: boolean;
  nextButtonIconUrl?: string;
  nextButtonShowTooltip?: boolean;
  nextButtonTooltipText?: string;
  /** Done button settings */
  showDone?: boolean;
  doneButtonText?: string;
  doneButtonShowIcon?: boolean;
  doneButtonIconUrl?: string;
  doneButtonShowTooltip?: boolean;
  doneButtonTooltipText?: string;
  /** Button alignment */
  buttonAlignment?: 'left' | 'center' | 'right' | 'space-between';
  /** Progress indicator */
  showProgressBar?: boolean;
  progressStyle?: 'bar' | 'steps' | 'percentage';
}

/**
 * Footer definition
 */
export interface FooterAPI {
  configuration: FooterConfigurationAPI;
}

// ============================================================================
// Shared Widgets Types
// ============================================================================

/**
 * YourBuild widget button configuration
 */
export interface YourBuildButtonConfigAPI {
  showText: boolean;
  buttonLabel: string;
  showIcon: boolean;
}

/**
 * YourBuild widget Add to Cart button configuration
 */
export interface YourBuildAddToCartConfigAPI extends YourBuildButtonConfigAPI {
  saveToPermanentStorage: boolean;
  addingToCartMessage?: string;
}

/**
 * YourBuild widget configuration (stored in sharedWidgets)
 */
export interface YourBuildWidgetConfigAPI {
  id: string;
  showTitle: boolean;
  title: string;
  showShareButton: boolean;
  shareButtonConfig: YourBuildButtonConfigAPI;
  showDescription: boolean;
  description: string;
  showAddToCartButton: boolean;
  addToCartButtonConfig: YourBuildAddToCartConfigAPI;
}

/**
 * Shared widgets storage - model-level widget configurations
 */
export interface SharedWidgetsAPI {
  [widgetKey: string]: YourBuildWidgetConfigAPI | Record<string, unknown>;
}

// ============================================================================
// Layout & UI Model Root Types
// ============================================================================

/**
 * Layout definition - contains all visual structure
 */
export interface LayoutAPI {
  /** Header configuration */
  header: HeaderAPI;
  /** Footer configuration (optional) */
  footer?: FooterAPI;
  /** Navigation structure */
  navigation: NavigationAPI;
  /** Canvas map (pageId -> canvas) */
  canvas: CanvasMapAPI;
  /** Shared widget configurations */
  sharedWidgets?: SharedWidgetsAPI;
}

/**
 * Editor state - not saved to API
 */
export interface EditorStateAPI {
  /** Currently active group ID */
  activeGroupId: string | null;
  /** Currently active page ID */
  activePageId: string | null;
  /** Currently selected container ID */
  selectedContainerId: string | null;
}

/**
 * Composition mode of a UI model — how strictly the builder polices submodel usage.
 */
export type CompositionMode = 'restricted' | 'freestyle';

/**
 * UI Model - THE ROOT TYPE
 * NOTE: Some properties are optional for backwards compatibility with legacy data
 */
export interface UIModelAPI {
  /** Unique identifier */
  id: string;
  /** Model display name */
  name: string;
  /** Channel identifier (optional for legacy compatibility) */
  channel_id?: string;
  /** Model spec ID (optional) */
  model_id?: string;
  /** Launch icon URL */
  launchIcon?: string;
  /** Creation timestamp (UNIX epoch) */
  createdAt?: number | string;
  /** Last update timestamp (UNIX epoch) */
  updatedAt?: number | string;
  /** Creator display name */
  createdBy?: string;
  /** Creator user ID (optional for legacy compatibility) */
  createdByUserID?: string;
  /** Creator username */
  createdByUsername?: string;
  /** Last updater user ID */
  updatedByUserID?: string;
  /** Last updater username */
  updatedByUsername?: string;
  /** Promotion status */
  is_promoted?: boolean;
  /** ISO string set when the UI model was last promoted */
  last_promoted_at?: string | null;
  /** True when is_promoted and updatedAt > last_promoted_at (unsaved changes since last promotion) */
  hasUnpromotedChanges?: boolean;
  /**
   * Composition mode. 'restricted' (or absent — every pre-existing model) keeps the
   * submodel-locking rules; 'freestyle' lifts them (mixing submodels, duplicates with
   * instance-suffixed ids). One-way: a freestyle model can never go back to restricted.
   */
  composition_mode?: CompositionMode;
  /** Layout definition */
  layout: LayoutAPI;
  /** Theme configuration */
  theme?: Record<string, unknown>;
  /** Custom CSS configuration */
  customCss?: Record<string, unknown>;
  /** Editor state (for resuming editing) */
  editorState?: EditorStateAPI;
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Generic API response wrapper
 */
export interface APIResponse<T> {
  done: boolean;
  data: T;
  message?: string;
}

/**
 * UI Model GET response
 */
export interface UIModelResponse {
  model: UIModelAPI;
}

/**
 * UI Model LIST response
 */
export interface UIModelListResponse {
  models: UIModelAPI[];
  total: number;
  page: number;
  limit: number;
}

// ============================================================================
// Request Types
// ============================================================================

/**
 * Create UI Model request
 */
export interface CreateUIModelRequest {
  channelId: string;
  name: string;
  modelId: string;
  launchIcon?: string;
}

/**
 * Update Header request
 */
export interface UpdateHeaderRequest {
  showHeader: boolean;
  showTitle: boolean;
  titleText: string;
  titleLayout: 'Centre' | 'Left' | 'Right';
  logoUrl: string;
  showResetButton: boolean;
  resetButtonText: string;
  resetButtonShowIcon: boolean;
  resetButtonIconUrl?: string;
  resetButtonShowTooltip: boolean;
  resetButtonTooltipText?: string;
  showCloseButton: boolean;
  closeButtonText: string;
  closeButtonShowIcon: boolean;
  closeButtonIconUrl?: string;
  closeButtonShowTooltip: boolean;
  closeButtonTooltipText?: string;
  showYourBuild?: boolean;
}

/**
 * Update Footer request
 */
export interface UpdateFooterRequest {
  showFooter: boolean;
  showDescription?: boolean;
  descriptionText?: string;
  showPreviousButton?: boolean;
  previousButtonText?: string;
  previousButtonShowIcon?: boolean;
  previousButtonIconUrl?: string;
  previousButtonShowTooltip?: boolean;
  previousButtonTooltipText?: string;
  showNextButton?: boolean;
  nextButtonText?: string;
  nextButtonShowIcon?: boolean;
  nextButtonIconUrl?: string;
  nextButtonShowTooltip?: boolean;
  nextButtonTooltipText?: string;
}

/**
 * Update Canvas request
 */
export interface UpdateCanvasRequest {
  containers: ContainerAPI[];
}

// ============================================================================
// Default Values
// ============================================================================

/**
 * Default container configuration
 * NOTE: All 8 properties are required for API compatibility
 */
export const DEFAULT_CONTAINER_CONFIGURATION: ContainerConfigurationAPI = {
  width: '100%',
  height: 'auto',
  alignment: 'left',
  layout: 'vertical',
  showTitle: true,
  widthLocked: false,
  behaveAsFilter: false,
  collapsible: false,
  collapseByDefault: false,
  showBorder: false,
  border: [],
};

/**
 * Default container metadata
 */
export const DEFAULT_CONTAINER_METADATA: ContainerMetadataAPI = {
  type: 'container',
  isNested: false,
  rowIndex: 0,
};

/**
 * Default submodel configuration
 */
export const DEFAULT_SUBMODEL_CONFIGURATION: SubmodelConfigurationAPI = {
  showTitle: true,
  hideIfNoProducts: false,
  exclusionType: 'hide',
  isVisible: true,
};

/**
 * Default header configuration
 */
export const DEFAULT_HEADER_CONFIGURATION: HeaderConfigurationAPI = {
  showHeader: true,
  showTitle: true,
  titleText: '',
  titleLayout: 'Centre',
  logoUrl: '',
  showResetButton: true,
  resetButtonText: 'Reset',
  resetButtonShowIcon: false,
  resetButtonShowTooltip: false,
  showCloseButton: true,
  closeButtonText: 'Close',
  closeButtonShowIcon: false,
  closeButtonShowTooltip: false,
  showYourBuild: false,
};

/**
 * Default footer configuration
 */
export const DEFAULT_FOOTER_CONFIGURATION: FooterConfigurationAPI = {
  showFooter: true,
  showDescription: false,
  descriptionText: '',
  showPreviousButton: true,
  previousButtonText: 'Previous',
  previousButtonShowIcon: false,
  previousButtonShowTooltip: false,
  showNextButton: true,
  nextButtonText: 'Next',
  nextButtonShowIcon: false,
  nextButtonShowTooltip: false,
  showDone: false,
  doneButtonText: 'Done',
  doneButtonShowIcon: false,
  doneButtonShowTooltip: false,
};

/**
 * Default navigation configuration
 */
export const DEFAULT_NAVIGATION_CONFIGURATION: NavigationConfigurationAPI = {
  orientation: 'Horizontal',
  style: 'Block',
  textAlignment: 'Centre',
  showDivider: true,
  pageSpacing: 8,
  groupSpacing: 16,
  flow: 'Nonlinear',
  incompleteOpeningPage: 'first-incomplete-page',
  completeOpeningPage: 'first-page-always',
};

// ============================================================================
// Drop Utilities
// ============================================================================

/**
 * Build filtered dataConfiguration for a field dropped from the component library.
 * Only multichoice fields get dataConfiguration (just choices, limited to maxChoices).
 * Non-multichoice fields return undefined (they don't need dataConfiguration).
 */
export const buildDropDataConfiguration = (
  fieldType: string,
  rawDataConfig?: Record<string, unknown>,
  maxChoices: number = 2
): FieldDataConfiguration | undefined => {
  const normalized = fieldType.toLowerCase().replace(/-/g, '');
  if (normalized !== 'multichoice' || !rawDataConfig?.choices) {
    return undefined;
  }
  const allChoices = rawDataConfig.choices as FieldChoiceOption[];
  return { choices: allChoices.slice(0, maxChoices) };
};

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Create a new container with default values
 * NOTE: Properly merges overrides for nested objects (configuration, metadata, content)
 */
export const createContainerAPI = (
  id: string,
  name: string,
  order: number,
  rowIndex: number = 0,
  overrides?: Partial<ContainerAPI>
): ContainerAPI => {
  const baseContainer: ContainerAPI = {
    id,
    name,
    order,
    rowIndex,
    configuration: {
      ...DEFAULT_CONTAINER_CONFIGURATION,
      ...overrides?.configuration,
    },
    content: {
      items: [],
      ...overrides?.content,
    },
    metadata: {
      ...DEFAULT_CONTAINER_METADATA,
      rowIndex,
      ...overrides?.metadata,
    },
    usageInfo: {
      usedComponents: [],
    },
  };

  // Apply top-level overrides (excluding nested objects already merged)
  if (overrides) {
    const { configuration, metadata, content, ...rest } = overrides;
    Object.assign(baseContainer, rest);
  }

  return baseContainer;
};

/**
 * Create a new group with default values
 */
export const createGroupAPI = (
  id: string,
  name: string,
  order: number,
  overrides?: Partial<GroupAPI>
): GroupAPI => ({
  id,
  name,
  order,
  orientation: 'horizontal',
  configuration: {
    showTitle: true,
    name,
  },
  pages: [],
  ...overrides,
});

/**
 * Create a new page with default values
 */
export const createPageAPI = (
  id: string,
  name: string,
  canvasId: string,
  order: number,
  overrides?: Partial<PageAPI>
): PageAPI => ({
  id,
  name,
  canvasId,
  order,
  configuration: {
    autoAdvance: false,
    name,
  },
  ...overrides,
});

/**
 * Create a new canvas with default values
 */
export const createCanvasAPI = (
  pageId: string,
  groupId: string,
  overrides?: Partial<CanvasAPI>
): CanvasAPI => ({
  pageId,
  groupId,
  containers: [],
  ...overrides,
});

/**
 * Create a new field item with default values
 */
export const createFieldAPI = (
  id: string,
  name: string,
  fieldType: FieldType,
  parentId?: string,
  overrides?: Partial<FieldAPI>
): FieldAPI => ({
  id,
  name,
  type: 'field',
  fieldType: (fieldType.toLowerCase().replace(/-/g, '') as FieldType) || 'text',
  isReusable: true,
  parentId,
  configuration: {},
  ...overrides,
});

/**
 * Create a new widget item with default values
 */
export const createWidgetAPI = (
  id: string,
  widgetType: WidgetType,
  parentId?: string,
  overrides?: Partial<WidgetAPI>
): WidgetAPI => ({
  id,
  type: 'widget',
  widgetType,
  isReusable: widgetType !== 'yourBuild',
  parentId,
  configuration: {},
  ...overrides,
});

/**
 * Create a new category item with default values
 */
export const createCategoryAPI = (
  id: string,
  name: string,
  categoryId: string,
  parentId?: string,
  overrides?: Partial<CategoryAPI>
): CategoryAPI => ({
  id,
  name,
  type: 'category',
  categoryId,
  isReusable: true,
  parentId,
  configuration: {},
  ...overrides,
});

/**
 * Create a new connector item with default values
 */
export const createConnectorAPI = (
  id: string,
  name: string,
  parentId?: string,
  overrides?: Partial<ConnectorAPI>
): ConnectorAPI => ({
  id,
  name,
  type: 'connector',
  isReusable: true,
  parentId,
  configuration: {},
  ...overrides,
});

/**
 * Create a full submodel with default configuration
 */
export const createSubmodelFullAPI = (
  id: string,
  name: string,
  overrides?: Partial<SubmodelFullAPI>
): SubmodelFullAPI => ({
  id,
  name,
  isTemporary: false,
  configuration: { ...DEFAULT_SUBMODEL_CONFIGURATION },
  ...overrides,
});

/**
 * Create a submodel reference (for nested containers)
 */
export const createSubmodelRefAPI = (id: string): SubmodelRefAPI => ({
  id,
});

// ============================================================================
// API Serialization Helpers (Bi-directional Compatibility)
// ============================================================================

/**
 * Parse container content from API response
 * Handles empty object {} and partial content structures
 */
const parseContainerContent = (raw: unknown): ContainerContentAPI => {
  if (!raw || typeof raw !== 'object' || Object.keys(raw as object).length === 0) {
    return { items: [] };
  }

  const content = raw as Partial<ContainerContentAPI>;
  return {
    items: content.items ?? [],
    submodel: content.submodel,
    nestedContainers: content.nestedContainers,
  };
};

/**
 * Serialize container content for API
 * Only includes non-empty properties to match API format
 * Returns {} if no content, otherwise { items, submodel?, nestedContainers? }
 */
export const serializeContainerContent = (
  content: ContainerContentAPI
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  if (content.items && content.items.length > 0) {
    result.items = content.items;
  }

  if (content.submodel) {
    result.submodel = content.submodel;
  }

  if (content.nestedContainers && content.nestedContainers.length > 0) {
    result.nestedContainers = content.nestedContainers;
  }

  return result;
};

/**
 * Serialize a container for API save
 * Ensures all required fields are present with correct structure
 *
 * NOTE: not currently on the live save path. Canvas saves go
 * ContainerConfigPanel -> useContainerOperations.updateContainer -> containerCommands
 * -> CanvasAutoSave -> SaveQueueContext -> UIModelProvider.handleSave -> UIModelApiService.updateCanvas,
 * which passes `configuration` through wholesale (spread-merge, then `JSON.stringify`). This helper is
 * exported and unit-tested, so it stays correct against the type in case it ever is wired in.
 *
 * The configuration below is built by spreading `container.configuration` FIRST, then re-stating only
 * the optional keys that need a concrete default for the API. Do not convert this back into a
 * hand-listed whitelist: as one it silently drifted twice behind the type — `collapseByDefault` was
 * added to the type in `ea6353bbc` and missed here, then `showBorder`/`border` were added and missed
 * too. A dropped key is invisible at the call site and produces a save that looks like it worked.
 * `__tests__/unit/types/apiSerializationHelpers.test.ts` pins the output to every key of
 * DEFAULT_CONTAINER_CONFIGURATION, so the type can no longer drift ahead of this function.
 */
export const serializeContainerForAPI = (
  container: ContainerAPI
): Record<string, unknown> => ({
  id: container.id,
  name: container.name,
  order: container.order,
  rowIndex: container.rowIndex ?? 0,
  configuration: {
    ...container.configuration,
    // Optional in the type, but the API expects a concrete value rather than `undefined`.
    widthLocked: container.configuration.widthLocked ?? false,
    behaveAsFilter: container.configuration.behaveAsFilter ?? false,
    collapsible: container.configuration.collapsible ?? false,
    collapseByDefault: container.configuration.collapseByDefault ?? false,
    showBorder: container.configuration.showBorder ?? false,
    border: container.configuration.border ?? [],
  },
  content: serializeContainerContent(container.content),
  metadata: container.metadata ?? {
    type: 'container',
    isNested: false,
    rowIndex: container.rowIndex ?? 0,
  },
  usageInfo: container.usageInfo,
});

/**
 * Parse a container from API response
 * Handles missing rowIndex, metadata, and partial content
 * @param raw - Raw container object from API
 * @param index - Fallback index for rowIndex/order if not present
 */
export const parseContainerFromAPI = (
  raw: Record<string, unknown>,
  index: number = 0
): ContainerAPI => {
  const rawContainer = raw as Partial<ContainerAPI>;
  const rowIndex =
    rawContainer.rowIndex ??
    (rawContainer.metadata as ContainerMetadataAPI | undefined)?.rowIndex ??
    index;

  return {
    id: rawContainer.id ?? `container-${Date.now()}`,
    name: rawContainer.name ?? `Container ${index + 1}`,
    order: rawContainer.order ?? index,
    rowIndex,
    configuration: {
      ...DEFAULT_CONTAINER_CONFIGURATION,
      ...rawContainer.configuration,
    },
    content: parseContainerContent(rawContainer.content),
    metadata: rawContainer.metadata ?? {
      type: 'container',
      isNested: false,
      rowIndex,
    },
    usageInfo: rawContainer.usageInfo ?? { usedComponents: [] },
  };
};

/**
 * Parse an array of containers from API response
 * Handles missing fields and ensures consistent structure
 */
export const parseContainersFromAPI = (
  rawContainers: unknown[]
): ContainerAPI[] => {
  if (!Array.isArray(rawContainers)) {
    return [];
  }

  return rawContainers.map((raw, index) =>
    parseContainerFromAPI(raw as Record<string, unknown>, index)
  );
};

/**
 * Serialize an array of containers for API save
 */
export const serializeContainersForAPI = (
  containers: ContainerAPI[]
): Record<string, unknown>[] => containers.map(serializeContainerForAPI);
